/**
 * DEUCE is the design language for Docket.
 *
 * TODO: Use the DEUCE constants to generate the CSS variables
 */

export const DEUCE = {
  COLORS: {
    PRIMARY: '#009CE6',
    PRIMARY_DARK: '#016EB1',
    TEXT_INVERT: '#FFF',
    TEXT_PRIMARY_HIGH_EMPHASIS: '#04406C',
    TEXT_MAIN_MEDIUM_EMPHASIS: '#03071299',
    TEXT_FIELD_BORDER_ENABLED: '#7B838D',
    TEXT_FIELD_BORDER_HOVER: '#373C49',
    TEXT_FIELD_BORDER_ACTIVATED: '#009CE6',
    TEXT_FIELD_BORDER_DISABLED: '#D2DCE3',
    TEXT_FIELD_BORDER_ERROR: '#C82D42',
    TEXT_FIELD_BORDER_POSITIVE: '#3A9E5C',
    PRIMARY_MEDIUM_EMPHASIS: '#016EB1',
    TEXT_FIELD_HEADING_COLOR_ACTIVATED: '#009CE6',
    TEXT_MAIN_HEADING_EMPHASIS: '#030712DE',
    TEXT_MAIN_HIGH_EMPHASIS: '#030712DE',
    SWITCHER_FILL_ENABLED: '#EDF1F6',
    SWITCHER_FILL_ACTIVATED: '#041449',
    SWITCHER_FILL_DISABLED: '#EDF1F6',
    SWITCHER_BORDER_ENABLED: '#041449',
    SWITCHER_BORDER_DISABLED: '#DCE4EA',
    SWITCHER_TEXT_ENABLED: '#041449',
    SWITCHER_TEXT_ACTIVATED: '#FFFFFF',
    BORDER_MAIN_COLOR: '#DCE4EA',
    SURFACE_PRIMARY_LIGHTER: '#E4F1F9',
    SURFACE_PRIMARY_LIGHT: '#D9E9F4',
    SURFACE_PRIMARY_LIGHT_HOVER: '#C9E0F0',
    SURFACE_PRIMARY: '#D1E2EF',
    SURFACE_PRIMARY_HOVER: '#C8D9E5',
    SURFACE_LIGHTEST: '#F4F6F9',
    SURFACE_LIGHTER: '#EDF1F6',
    SURFACE_LIGHT: '#E6EBF2',
    SURFACE_MEDIUM: '#D2DCE3',
    SURFACE_DARK: '#C8D0D8',
    SURFACE_DARKER: '#A2ACB4',
    SURFACE_PURE_WHITE: '#FFFFFF',
    SURFACE_PURE_WHITE_HOVER: '#F5F5F5',
    NEGATIVE_COLOR: '#C82D42',
    SURFACE_NEGATIVE_LIGHT: '#FAEAEC',
    SURFACE_WARNING_LIGHT: '#FFF3D1',
    DATA_TABLE_FILL_HEADER: '#EDF1F6',
    DATA_TABLE_BORDER_HEADER: '#A2ACB4',
    DATA_TABLE_BORDER: '#E1E1E1',
    TOGGLE_ON: '#009CE6',
    TOGGLE_ON_HOVER: '#016EB1',
    TOGGLE_OFF: '#C8D5DB',
    TOGGLE_OFF_HOVER: '#A2ACB4',
  },
  PROPS: {
    GLOBAL_BORDER_RADIUS: '8px',
    GLOBAL_FONT_WEIGHT: '500',
    GLOBAL_FONT_WEIGHT_BOLD: '600',
    GLOBAL_FONT_WEIGHT_BOLDER: '700',
    GLOBAL_FONT_SIZE: '14px',
    GLOBAL_FONT_FAMILY: 'Inter',
    TEXT_FIELD_BORDER_RADIUS: '8px',
    TEXT_FIELD_BORDER_WIDTH: '1px',
    TEXT_FIELD_BORDER_WIDTH_ACTIVATED: '2px',
    TEXT_FIELD_HEADING_FONT_FAMILY: 'Inter',
    TEXT_FIELD_HEADING_FONT_WEIGHT: '500',
    TEXT_FIELD_HEADING_FONT_SIZE: '10px',
    TEXT_FIELD_HEADING_LINE_HEIGHT: '110%',
    TEXT_FIELD_HEADING_LETTER_SPACING: '0px',
    TEXT_FIELD_FONT_FAMILY: 'Inter',
    TEXT_FIELD_FONT_WEIGHT: '500',
    TEXT_FIELD_FONT_SIZE: '12px',
    TEXT_FIELD_LINE_HEIGHT: '117%',
    TEXT_FIELD_LETTER_SPACING: '0px',
    TEXT_FIELD_VERTICAL_ALIGN: 'middle',
    POPUP_BOX_SHADOW: '0px 4px 36px 0px #50505040',
    POPUP_BOX_SHADOW_LESSER: '0px 4px 18px 0px #50505040',
    SWITCHER_BORDER_RADIUS: '6px',
    BUTTON_TERTIARY_BORDER: '0px 0px 0px 1px #7B838D inset',
    BUTTON_TERTIARY_BORDER_HOVER: '0px 0px 0px 1px #373C49 inset',
    BUTTON_TERTIARY_BORDER_ACTIVATED: '0px 0px 0px 2px #04406C inset',
  },
};

const themeProperties = {
  colors: {
    '--background': 'rgb(240, 243, 246)',
    '--app-background': 'DEUCE.COLORS.BORDER_MAIN_COLOR',
    '--theme': '#009ce6',
    '--shade': 'rgba(0,0,0,0.10)',
    '--shade-bold': 'rgba(0,0,0,0.3)',
    '--shade-half': 'rgba(0,0,0,0.005)',
    '--film-bold': 'rgba(255,255,255,0.3)',
    '--alt-theme': 'green',
    '--highlight': '#ffffd7',
    '--selection': 'rgb(0,122,255)',
    '--input': 'rgb(252,252,252)',
    '--dropdown': 'linear-gradient(180deg, rgb(255,255,255), rgb(255,255,255))',
    '--neon-green': '#7eb10f',
    '--red': 'rgb(255,59,48)',
    '--orange': 'rgb(235,129,0)',
    '--yellow': 'rgb(255,204,0)',
    '--green': 'rgb(52, 199, 89)',
    '--mint': 'rgb(0,199,190)',
    '--teal': 'rgb(48,176,199)',
    '--cyan': 'rgb(50,173,230)',
    '--white': DEUCE.COLORS.SURFACE_PURE_WHITE,
    '--blue': 'rgb(0,122,255)',
    '--indigo': 'rgb(88,86,214)',
    '--purple': 'rgb(175,82,222)',
    '--pink': 'rgb(255,45,85)',
    '--brown': 'rgb(162,132,94)',
    '--light-grey': 'rgb(123,131,141)',
  },
  props: {
    '--global-border-radius': DEUCE.PROPS.GLOBAL_BORDER_RADIUS,
    '--global-font-weight': DEUCE.PROPS.GLOBAL_FONT_WEIGHT,
    '--global-font-weight-bold': DEUCE.PROPS.GLOBAL_FONT_WEIGHT_BOLD,
    '--global-font-weight-bolder': DEUCE.PROPS.GLOBAL_FONT_WEIGHT_BOLDER,
    '--global-font-size': DEUCE.PROPS.GLOBAL_FONT_SIZE,
    '--global-font-family': DEUCE.PROPS.GLOBAL_FONT_FAMILY,
    '--primary-color': DEUCE.COLORS.PRIMARY,
    '--primary-dark-color': DEUCE.COLORS.PRIMARY_DARK,
    '--text-invert-color': DEUCE.COLORS.TEXT_INVERT,
    '--primary-medium-emphasis': DEUCE.COLORS.PRIMARY_MEDIUM_EMPHASIS,
    '--text-field-border-radius': DEUCE.PROPS.TEXT_FIELD_BORDER_RADIUS,
    '--text-field-border-width': DEUCE.PROPS.TEXT_FIELD_BORDER_WIDTH,
    '--text-field-border-width-activated':
      DEUCE.PROPS.TEXT_FIELD_BORDER_WIDTH_ACTIVATED,
    '--text-field-heading-color-activated':
      DEUCE.COLORS.TEXT_FIELD_HEADING_COLOR_ACTIVATED,
    '--text-field-heading-font-family':
      DEUCE.PROPS.TEXT_FIELD_HEADING_FONT_FAMILY,
    '--text-field-heading-font-weight':
      DEUCE.PROPS.TEXT_FIELD_HEADING_FONT_WEIGHT,
    '--text-field-heading-font-size': DEUCE.PROPS.TEXT_FIELD_HEADING_FONT_SIZE,
    '--text-field-heading-line-height':
      DEUCE.PROPS.TEXT_FIELD_HEADING_LINE_HEIGHT,
    '--text-field-heading-letter-spacing':
      DEUCE.PROPS.TEXT_FIELD_HEADING_LETTER_SPACING,
    '--text-primary-high-emphasis': DEUCE.COLORS.TEXT_PRIMARY_HIGH_EMPHASIS,
    '--text-field-font-family': DEUCE.PROPS.TEXT_FIELD_FONT_FAMILY,
    '--text-field-font-weight': DEUCE.PROPS.TEXT_FIELD_FONT_WEIGHT,
    '--text-field-font-size': DEUCE.PROPS.TEXT_FIELD_FONT_SIZE,
    '--text-field-line-height': DEUCE.PROPS.TEXT_FIELD_LINE_HEIGHT,
    '--text-field-letter-spacing': DEUCE.PROPS.TEXT_FIELD_LETTER_SPACING,
    '--text-field-vertical-align': DEUCE.PROPS.TEXT_FIELD_VERTICAL_ALIGN,
    '--popup-box-shadow': DEUCE.PROPS.POPUP_BOX_SHADOW,
    '--popup-box-shadow-lesser': DEUCE.PROPS.POPUP_BOX_SHADOW_LESSER,
    '--text-field-border-enabled': DEUCE.COLORS.TEXT_FIELD_BORDER_ENABLED,
    '--text-field-border-hover': DEUCE.COLORS.TEXT_FIELD_BORDER_HOVER,
    '--text-field-border-activated': DEUCE.COLORS.TEXT_FIELD_BORDER_ACTIVATED,
    '--text-field-border-disabled': DEUCE.COLORS.TEXT_FIELD_BORDER_DISABLED,
    '--text-field-border-error': DEUCE.COLORS.TEXT_FIELD_BORDER_ERROR,
    '--text-field-border-positive': DEUCE.COLORS.TEXT_FIELD_BORDER_POSITIVE,
    '--text-main-medium-emphasis': DEUCE.COLORS.TEXT_MAIN_MEDIUM_EMPHASIS,
    '--text-main-heading-emphasis': DEUCE.COLORS.TEXT_MAIN_HEADING_EMPHASIS,
    '--text-main-high-emphasis': DEUCE.COLORS.TEXT_MAIN_HIGH_EMPHASIS,
    '--switcher-border-radius': DEUCE.PROPS.SWITCHER_BORDER_RADIUS,
    '--switcher-fill-enabled': DEUCE.COLORS.SWITCHER_FILL_ENABLED,
    '--switcher-fill-activated': DEUCE.COLORS.SWITCHER_FILL_ACTIVATED,
    '--switcher-fill-disabled': DEUCE.COLORS.SWITCHER_FILL_DISABLED,
    '--switcher-border-enabled': DEUCE.COLORS.SWITCHER_BORDER_ENABLED,
    '--switcher-border-disabled': DEUCE.COLORS.SWITCHER_BORDER_DISABLED,
    '--switcher-text-enabled': DEUCE.COLORS.SWITCHER_TEXT_ENABLED,
    '--switcher-text-activated': DEUCE.COLORS.SWITCHER_TEXT_ACTIVATED,
    '--border-main-color': DEUCE.COLORS.BORDER_MAIN_COLOR,
    '--button-tertiary-border': DEUCE.PROPS.BUTTON_TERTIARY_BORDER,
    '--button-tertiary-border-hover': DEUCE.PROPS.BUTTON_TERTIARY_BORDER_HOVER,
    '--button-tertiary-border-activated':
      DEUCE.PROPS.BUTTON_TERTIARY_BORDER_ACTIVATED,
    '--surface-primary-lighter': DEUCE.COLORS.SURFACE_PRIMARY_LIGHTER,
    '--surface-primary-light': DEUCE.COLORS.SURFACE_PRIMARY_LIGHT,
    '--surface-primary-light-hover': DEUCE.COLORS.SURFACE_PRIMARY_LIGHT_HOVER,
    '--surface-primary': DEUCE.COLORS.SURFACE_PRIMARY,
    '--surface-primary-hover': DEUCE.COLORS.SURFACE_PRIMARY_HOVER,
    '--surface-lightest': DEUCE.COLORS.SURFACE_LIGHTEST,
    '--surface-lighter': DEUCE.COLORS.SURFACE_LIGHTER,
    '--surface-light': DEUCE.COLORS.SURFACE_LIGHT,
    '--surface-medium': DEUCE.COLORS.SURFACE_MEDIUM,
    '--surface-dark': DEUCE.COLORS.SURFACE_DARK,
    '--surface-darker': DEUCE.COLORS.SURFACE_DARKER,
    '--surface-pure-white': DEUCE.COLORS.SURFACE_PURE_WHITE,
    '--surface-pure-white-hover': DEUCE.COLORS.SURFACE_PURE_WHITE_HOVER,
    '--negative-color': DEUCE.COLORS.NEGATIVE_COLOR,
    '--surface-negative-light': DEUCE.COLORS.SURFACE_NEGATIVE_LIGHT,
    '--surface-warning-light': DEUCE.COLORS.SURFACE_WARNING_LIGHT,
    '--data-table-fill-header': DEUCE.COLORS.DATA_TABLE_FILL_HEADER,
    '--data-table-border-header': DEUCE.COLORS.DATA_TABLE_BORDER_HEADER,
    '--data-table-border': DEUCE.COLORS.DATA_TABLE_BORDER,
    '--toggle-on': DEUCE.COLORS.TOGGLE_ON,
    '--toggle-on-hover': DEUCE.COLORS.TOGGLE_ON_HOVER,
    '--toggle-off': DEUCE.COLORS.TOGGLE_OFF,
    '--toggle-off-hover': DEUCE.COLORS.TOGGLE_OFF_HOVER,
  },
  assets: {
    PRODUCT_NAME: 'Docket',
  },
};

const darkThemeProperties = {
  colors: {
    '--background': 'rgb(30,45,60)',
    '--theme': '#1e5ead',
    '--shade': 'rgba(255,255,255,0.10)',
    '--shade-bold': 'rgba(255,255,255,0.3)',
    '--shade-half': 'rgba(255,255,255,0.005)',
    '--film-bold': 'rgba(255,255,255,0.3)',
    '--selection': 'rgb(30,155,255)',
    '--input': 'rgb(0,0,0,0.2)',
    '--dropdown': 'linear-gradient(180deg, rgb(52,67,82), rgb(52,67,82))',
    '--alt-theme': 'green',
    '--highlight': '#f4e55f',
    '--neon-green': '#7eb10f',
    '--red': 'rgb(255,59,48)',
    '--orange': 'rgb(235,129,0)',
    '--yellow': 'rgb(255,204,0)',
    '--green': 'rgb(52, 199, 89)',
    '--white': 'rgb(255,255,255)',
    '--mint': 'rgb(0,199,190)',
    '--teal': 'rgb(48,176,199)',
    '--cyan': 'rgb(50,173,230)',
    '--blue': 'rgb(0,122,255)',
    '--indigo': 'rgb(88,86,214)',
    '--purple': 'rgb(175,82,222)',
    '--pink': 'rgb(255,45,85)',
    '--brown': 'rgb(162,132,94)',
    '--light-grey': 'rgb(123,131,141)',
  },
};

let fontProperties = {
  '--font-family': 'Inter',
  '--font-size-1': '28px',
  '--font-size-2': '20px',
  '--font-size-3': '16px',
  '--font-size-4': '14px',
  '--font-size-5': '12px',
  '--font-size-6': '10px',
  '--font-weight-1': '800',
  '--font-weight-2': '700',
  '--font-weight-3': '700',
  '--font-weight-4': '600',
  '--font-weight-5': '600',
  '--font-weight-6': '500',
  '--font-color-1': 'var(--background-text-color)',
  '--font-color-2': 'var(--background-text-color)',
  '--font-color-3': 'var(--background-text-color)',
  '--font-color-4': 'var(--background-text-color)',
  '--font-color-5': 'var(--background-secondary-text-color)',
  '--font-color-6': 'var(--background-tertiary-text-color)',
};

import tinycolor from 'tinycolor2';
const root = document.documentElement;

export class ThemeController {
  updateRootStyle(key, value) {
    root.style.setProperty(key, value);
  }

  update() {
    let themeProps =
      localStorage.dark === 'true' ? darkThemeProperties : themeProperties;

    Object.keys(themeProps.colors).forEach(property => {
      let prop = themeProps.colors[property];
      if (prop.startsWith('--')) {
        prop = getComputedStyle(document.documentElement)
          .getPropertyValue(prop)
          .trim();
      }

      if (tinycolor(prop).isValid()) {
        // Is a color
        let dark = tinycolor(prop).darken(10);
        let darkish = tinycolor(prop).darken(4);
        let darker = tinycolor(prop).darken(20);
        let darkest = tinycolor(prop).darken(30);
        let bright = tinycolor(prop).brighten(10);
        let brightish = tinycolor(prop).brighten(2);
        let brighter = tinycolor(prop).brighten(20);
        let brightest = tinycolor(prop).brighten(30);

        this.updateRootStyle(`${property}-color`, prop);
        this.updateRootStyle(
          `${property}-color-darkish`,
          darkish.toRgbString(),
        );
        this.updateRootStyle(`${property}-color-dark`, dark.toRgbString());
        this.updateRootStyle(`${property}-color-hover`, dark.toRgbString());
        this.updateRootStyle(`${property}-color-darker`, darker.toRgbString());
        this.updateRootStyle(
          `${property}-color-darkest`,
          darkest.toRgbString(),
        );
        this.updateRootStyle(
          `${property}-color-brightish`,
          brightish.toRgbString(),
        );
        this.updateRootStyle(`${property}-color-bright`, bright.toRgbString());
        this.updateRootStyle(
          `${property}-color-brighter`,
          brighter.toRgbString(),
        );
        this.updateRootStyle(
          `${property}-color-brightest`,
          brightest.toRgbString(),
        );

        // Color is perceived as bright/dark, text colors should be inverse
        if (tinycolor(prop).isLight()) {
          this.updateRootStyle(`${property}-raised-color`, dark.toRgbString());
          this.updateRootStyle(
            `${property}-raised-color-hover`,
            darker.toRgbString(),
          );
          this.updateRootStyle(`${property}-text-color`, '#222');
          this.updateRootStyle(`${property}-secondary-text-color`, '#444');
          this.updateRootStyle(`${property}-tertiary-text-color`, '#666');
          this.updateRootStyle(`${property}-accent-color`, 'rgba(3,0,12,0.1)');
          this.updateRootStyle(
            `${property}-accent-color-half`,
            'rgba(3,0,12,0.065)',
          );
          this.updateRootStyle(
            `${property}-inverse-accent-color`,
            'rgba(255,255,255,0.1)',
          );
          this.updateRootStyle(
            `${property}-inverse-accent-color-hover`,
            'rgba(255,255,255,0.2)',
          );
          this.updateRootStyle(
            `${property}-inverse-accent-color-half`,
            'rgba(255,255,255,0.065)',
          );
          this.updateRootStyle(
            `${property}-inverse-accent-color-hover-half`,
            'rgba(255,255,255,0.15)',
          );
        } else {
          this.updateRootStyle(
            `${property}-raised-color`,
            bright.toRgbString(),
          );
          this.updateRootStyle(
            `${property}-raised-color-hover`,
            brighter.toRgbString(),
          );
          this.updateRootStyle(`${property}-text-color`, '#FFF');
          this.updateRootStyle(`${property}-secondary-text-color`, '#EEE');
          this.updateRootStyle(`${property}-tertiary-text-color`, '#DDD');
          this.updateRootStyle(
            `${property}-accent-color`,
            'rgba(255,255,255,0.1)',
          );
          this.updateRootStyle(
            `${property}-accent-color-half`,
            'rgba(255,255,255,0.065)',
          );
          this.updateRootStyle(
            `${property}-inverse-accent-color`,
            'rgba(3,0,12,0.1)',
          );
          this.updateRootStyle(
            `${property}-inverse-accent-color-hover`,
            'rgba(0,0,0,0.2)',
          );
          this.updateRootStyle(
            `${property}-inverse-accent-color-half`,
            'rgba(3,0,12,0.065)',
          );
          this.updateRootStyle(
            `${property}-inverse-accent-color-hover-half`,
            'rgba(0,0,0,0.15)',
          );
        }
      }
    });

    Object.keys(themeProps.props).forEach(property => {
      let prop = themeProps.props[property];
      this.updateRootStyle(`${property}`, prop);
    });

    Object.keys(fontProperties).forEach(property => {
      let prop = fontProperties[property];
      this.updateRootStyle(`${property}`, prop);
    });

    this.updateRootStyle(`--selection-text-color`, 'white');
    this.updateRootStyle(`--selection-secondary-text-color`, 'white');
    this.updateRootStyle(`--selection-tertiary-text-color`, 'white');
  }
}

export default ThemeController;
